import React, { useEffect, useRef, useState } from "react";
import {
  Button,
  Input,
  Select,
  Space,
  Spin,
  message,
  Progress,
  Modal,
} from "antd";
import "./index.css";
import PdfFile from "../../../assets/img/pdfFiles.png";
import PlainText from "../../../assets/img/plainTextIcon.png";
import RichTextFormat from "../../../assets/img/richtextFormat.png";
import UploadIcon from "../../../assets/img/upload.png";
import LeftArrow from "../../../assets/img/icon-arrow-left.png";
import {
  DeleteOutlined,
  EyeOutlined,
  UploadOutlined,
  EditOutlined,
} from "@ant-design/icons";
import WordFile from "../../../assets/img/wordFile.png";
import ExcelFile from "../../../assets/img/excel.png";
import type { ColumnsType } from "antd/es/table";
import type { RcFile } from "antd/es/upload";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import GenericTable from "../../GeneralTable/GenericTable";
import TextSelector from "../DocsEditor/TextSelector";
import AppModal from "../../modals/AppModal";

// media redux
import { useAppDispatch, useAppSelector } from "../../../store/hooks";
import {
  setMediaProjectId,
  setMediaUsername,
  setMediaType,
  setMediaPage,
  setMediaPageSize,
  fetchMedia,
  openMediaDocument,
  clearViewing,
  setUploadProgress,
  uploadInternalMedia,
  uploadExternalMedia,
  setDeleting,
  deleteMedia,
  getDocumentById,
  setEditing,
  updateDocumentName,
} from "./media.slice";
import { markHighlightsDirty } from "../Highlights/highlights.slice";
import {
  fetchHomeSummary,
  fetchHomeDocuments,
  setHomeProjectId,
} from "../Home/home.slice";
import { useExporter } from "../../../hooks/useExporter";
import { getUserRole } from "../../../hooks/getUserRoles";

const { Search } = Input;
const { Option } = Select;

const allowedExtensions = [
  ".doc",
  ".docx",
  ".pdf",
  ".xls",
  ".xlsx",
  ".txt",
  ".rtf",
];

// ✅ Max file size për media upload (25MB)
const MAX_MEDIA_SIZE_MB = 25;
const MAX_MEDIA_SIZE_BYTES = MAX_MEDIA_SIZE_MB * 1024 * 1024;

const Media: React.FC = () => {
  const { t } = useTranslation("docProcessing");
  const { id: projectId } = useParams();
  const dispatch = useAppDispatch();

  useEffect(() => {
    dispatch(clearViewing());

    return () => {
      dispatch(clearViewing());
    };
  }, [dispatch]);

  const {
    items,
    total,
    page,
    pageSize,
    loading,
    filterUsername,
    documentType,
    viewingHtml,
    viewingLoading,
    viewingDocId,
    uploading,
    uploadProgress,
    deletingId,
    editingId,
    editLoading,
    updateLoading,
  } = useAppSelector((s) => s.media);

  const totalCodes = useAppSelector((s) => s.home.summary.total_codes);

  const userRole = getUserRole();
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [mediaTypeLocal, setMediaTypeLocal] = useState<
    "internal" | "external" | null
  >(null);
  const [fileList, setFileList] = useState<RcFile[]>([]);
  const [selectedFileType, setSelectedFileType] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [attachingExt, setAttachingExt] = useState(false);
  const [deletingLoading, setDeletingLoading] = useState(false);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [editName, setEditName] = useState("");

  const { exporting, exportNow } = useExporter({
    endpoint: "documents/export-excel",
    fallbackPrefix: "media",
    ext: "xlsx",
  });

  useEffect(() => {
    dispatch(setMediaProjectId(projectId ?? null));
  }, [dispatch, projectId]);

  useEffect(() => {
    if (!projectId) return;
    dispatch(fetchMedia());
  }, [dispatch, projectId, page, pageSize, filterUsername, documentType]);

  useEffect(() => {
    if (!projectId) return;
    dispatch(setHomeProjectId(projectId));
    dispatch(fetchHomeSummary({ projectId }));
  }, [dispatch, projectId]);

  const openDocument = (payload: { fileKey: string; documentId: string }) => {
    if (!totalCodes) {
      message.warning(t("media.noCodesWarning"));
      return;
    }
    dispatch(openMediaDocument(payload))
      .unwrap()
      .catch((err) =>
        message.error(err?.message || t("media.openDocumentError")),
      );
  };

  const isAllowedFile = (file: File) => {
    const ext = file.name.substring(file.name.lastIndexOf(".")).toLowerCase();
    return allowedExtensions.includes(ext);
  };

  const columns: ColumnsType<any> = [
    {
      title: t("media.table.username"),
      dataIndex: "username",
      key: "username",
      sorter: (a, b) => (a.username || "").localeCompare(b.username || ""),
    },
    {
      title: t("media.table.documentName"),
      dataIndex: "document_name",
      key: "document_name",
      sorter: (a, b) =>
        (a.document_name || "").localeCompare(b.document_name || ""),
    },
    {
      title: t("media.table.documentType"),
      dataIndex: "document_type",
      key: "document_type",
      sorter: (a, b) =>
        (a.document_type || "").localeCompare(b.document_type || ""),
      render: (val?: string) => {
        const v = (val || "").toLowerCase();
        const label =
          v === "internal"
            ? t("media.documentTypeInternal")
            : v === "external"
              ? t("media.documentTypeExternal")
              : v
                ? v[0].toUpperCase() + v.slice(1)
                : t("common.notAvailable");
        const cls =
          v === "internal"
            ? "pill internal"
            : v === "external"
              ? "pill external"
              : "pill";
        return <span className={cls}>{label}</span>;
      },
    },
    {
      title: t("media.table.codes"),
      dataIndex: "codes",
      key: "codes",
      sorter: (a, b) => (a.codes || 0) - (b.codes || 0),
    },
    {
      title: t("media.table.dateCreated"),
      dataIndex: "dateCreated",
      key: "dateCreated",
      sorter: (a, b) => (a._dateTs || 0) - (b._dateTs || 0),
    },
    {
      title: t("media.table.highlightLength"),
      dataIndex: "highlightLength",
      key: "highlightLength",
      sorter: (a, b) => (a.highlightLength || 0) - (b.highlightLength || 0),
    },
    {
      title: t("media.table.modifiedBy"),
      dataIndex: "modifiedBy",
      key: "modifiedBy",
      sorter: (a, b) => (a.modifiedBy || "").localeCompare(b.modifiedBy || ""),
    },
    {
      title: "",
      key: "actions",
      fixed: items?.length > 0 ? "right" : undefined,
      render: (_: any, doc: any) => (
        <Space size="middle">
          <EyeOutlined
            onClick={(e) => {
              e.stopPropagation();
              openDocument({
                fileKey: doc.file_key_id,
                documentId: doc.document_id || doc.key,
              });
            }}
            style={{ fill: "#A072EB", color: "#A072EB", cursor: "pointer" }}
          />
          <EditOutlined
            // onClick={(e) => {
            //   e.stopPropagation();
            //   setIsEditOpen(true);
            //   setEditName("");
            // }}
            onClick={(e) => {
              e.stopPropagation();

              dispatch(setEditing(doc.document_id || doc.key));
              setIsEditOpen(true);

              // optional: e vendos fillimisht nga tabela (instant)
              setEditName(doc.document_name || "");

              // ✅ pastaj e merr name-in e sakte nga API (source of truth)
              dispatch(getDocumentById(doc.document_id || doc.key))
                .unwrap()
                .then((data) => setEditName(data?.name ?? ""))
                .catch((err) =>
                  message.error(err?.message || t("media.loadDocumentError")),
                );
            }}
            style={{ fill: "#A072EB", color: "#A072EB", cursor: "pointer" }}
          />

          <DeleteOutlined
            onClick={(e) => {
              e.stopPropagation();
              dispatch(setDeleting(doc.document_id));
            }}
            style={{ fill: "#A072EB", color: "#A072EB", cursor: "pointer" }}
          />
        </Space>
      ),
    },
  ];

  const triggerFilePicker = (ext: string | string[]) => {
    setSelectedFileType(typeof ext === "string" ? ext : ext.join(","));
    setTimeout(() => fileInputRef.current?.click(), 0);
  };

  const afterMediaMutationRefreshHome = async () => {
    if (!projectId) return;
    dispatch(setHomeProjectId(projectId));
    dispatch(fetchHomeSummary({ projectId }));
    dispatch(fetchHomeDocuments({ projectId }));
  };

  const uploadFileToServer = async () => {
    const userData = localStorage.getItem("user");
    const userId = userData ? JSON.parse(userData)?.user_id : null;
    if (!projectId || !userId || fileList.length === 0) {
      message.warning(t("media.missingFileOrUser"));
      return;
    }
    const file = fileList[0] as File;

    // ✅ Guard për madhësinë para dispatch
    if (file.size > MAX_MEDIA_SIZE_BYTES) {
      message.error(t("media.fileSizeExceeds", { size: MAX_MEDIA_SIZE_MB }));
      setFileList([]);
      dispatch(setUploadProgress(0));
      return;
    }

    try {
      await dispatch(
        uploadInternalMedia({
          projectId,
          userId,
          file,
          onProgress: (p) => dispatch(setUploadProgress(p)),
        }),
      ).unwrap();

      message.success(t("media.uploadSuccess"));
      setFileList([]);
      setIsModalVisible(false);
      setMediaTypeLocal(null);

      await dispatch(fetchMedia());
      await afterMediaMutationRefreshHome();
    } catch (e: any) {
      message.error(e?.message || t("media.uploadError"));
    }
  };

  const attachExternal = async (url: string) => {
    const userData = localStorage.getItem("user");
    const userId = userData ? JSON.parse(userData)?.user_id : null;
    if (!projectId || !userId)
      return message.error(t("media.missingProjectOrUser"));
    if (!url) return;
    try {
      setAttachingExt(true);
      await dispatch(uploadExternalMedia({ projectId, userId, url })).unwrap();
      message.success(t("media.externalUploadSuccess"));
      setIsModalVisible(false);
      setMediaTypeLocal(null);

      await dispatch(fetchMedia());
      await afterMediaMutationRefreshHome();
    } catch (e: any) {
      message.error(e?.message || t("media.externalUploadError"));
    } finally {
      setAttachingExt(false);
    }
  };

  const handleConfirmDeleteDoc = async () => {
    if (!deletingId) return;
    try {
      setDeletingLoading(true);
      await dispatch(deleteMedia(deletingId)).unwrap();
      message.success(t("media.deleteSuccess"));
      // mbyll modalin
      dispatch(setDeleting(null));
      await dispatch(fetchMedia());
      await afterMediaMutationRefreshHome();
    } catch (e: any) {
      message.error(e?.message || t("media.deleteError"));
    } finally {
      setDeletingLoading(false);
    }
  };

  const handleExportMedia = async () => {
    if (!projectId) return;
    await exportNow({ params: { project_id: projectId } });
  };

  const handleSaveEdit = () => {
    if (!editingId) return;

    const name = editName.trim();
    if (!name) {
      message.warning(t("media.nameRequired"));
      return;
    }

    dispatch(updateDocumentName({ documentId: editingId, name }))
      .unwrap()
      .then((res: any) => {
        message.success(res?.message || t("media.updateSuccess"));
        setIsEditOpen(false);
        dispatch(setEditing(null));
        setEditName("");
      })
      .catch((err: any) => message.error(err?.message || t("media.updateError")));
  };

  return (
    <div className="media-container">
      {viewingLoading ? (
        <div className="media-innercontainer">
          <Spin size="large" tip={t("media.loadingDocument")} />
        </div>
      ) : !viewingHtml ? (
        <>
          <div className="media-header">
            <h2>{t("media.totalDocuments", { value: items.length })}</h2>
            {userRole !== "Technical Expert" && (
              <div className="media-actions">
                <Button className="exportbtn" onClick={handleExportMedia}>
                  <UploadOutlined className="exportIcon" />
                  {exporting ? t("media.exporting") : t("media.export")}
                </Button>
                <Button
                  className="add-media"
                  onClick={() => setIsModalVisible(true)}
                >
                  {t("media.addMedia")}
                </Button>
              </div>
            )}
          </div>

          <div className="media-filters">
            <Search
              placeholder={t("media.searchByUsername")}
              allowClear
              style={{ width: 300 }}
              value={filterUsername}
              onChange={(e) => dispatch(setMediaUsername(e.target.value))}
              onSearch={(val) => dispatch(setMediaUsername(val))}
            />
            <Select
              placeholder={t("media.filterByType")}
              style={{ width: 300 }}
              value={documentType}
              onChange={(val) => dispatch(setMediaType(val))}
              allowClear
            >
              <Option value="internal">{t("media.documentTypeInternal")}</Option>
              <Option value="external">{t("media.documentTypeExternal")}</Option>
            </Select>
          </div>

          <GenericTable<any>
            rowSelection={{ type: "checkbox" }}
            columns={columns}
            dataSource={items}
            loading={loading}
            emptyText={t("media.emptyText")}
            pagination={{
              current: page,
              pageSize,
              total,
              onChange: (p, ps) => {
                dispatch(setMediaPage(p));
                dispatch(setMediaPageSize(ps));
              },
            }}
            onRowClick={(record) =>
              openDocument({
                fileKey: record.file_key_id,
                documentId: record.document_id,
              })
            }
          />
        </>
      ) : (
        <TextSelector
          htmlContent={viewingHtml}
          onBack={() => dispatch(clearViewing())}
          projectId={projectId}
          documentId={viewingDocId ?? null}
          onHighlightChanged={() => dispatch(markHighlightsDirty())}
        />
      )}

      <input
        ref={fileInputRef}
        type="file"
        hidden
        accept={selectedFileType || allowedExtensions.join(",")}
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (!file) return;

          if (!isAllowedFile(file)) {
            message.error(t("media.allowedTypes"));
            e.target.value = "";
            return;
          }

          // ✅ limit i madhësisë në input
          if (file.size > MAX_MEDIA_SIZE_BYTES) {
            message.error(t("media.fileSizeExceeds", { size: MAX_MEDIA_SIZE_MB }));
            e.target.value = "";
            setFileList([]);
            return;
          }

          setFileList([file as RcFile]);
        }}
      />

      <Modal
        title={t("media.modal.addMediaTitle")}
        open={isModalVisible}
        onCancel={() => {
          setIsModalVisible(false);
          setMediaTypeLocal(null);
          setFileList([]);
          dispatch(setUploadProgress(0));
        }}
        footer={null}
        className="add-media-modal"
        style={{ minWidth: "700px" }}
      >
        {!mediaTypeLocal ? (
          <div style={{ textAlign: "center" }}>
            <p>{t("media.modal.chooseDocumentType")}</p>
            <Space
              direction="horizontal"
              size="large"
              className="buttons-container"
            >
              <Button
                onClick={() => setMediaTypeLocal("external")}
                className="external-documentButton"
              >
                {t("media.modal.externalDocumentLink")}
              </Button>
              <Button
                onClick={() => setMediaTypeLocal("internal")}
                className="upload-from-computerButton"
              >
                {t("media.modal.uploadFromComputer")}
              </Button>
            </Space>
          </div>
        ) : mediaTypeLocal === "internal" ? (
          <>
            <p>{t("media.modal.chooseFile")}</p>
            <div
              className="file-format-options"
              style={{
                display: "flex",
                gap: 20,
                justifyContent: "center",
                marginBottom: "2rem",
              }}
            >
              <img
                src={PdfFile}
                alt="PDF"
                onClick={() => triggerFilePicker(".pdf")}
                style={{ width: 110, height: 138, cursor: "pointer" }}
              />
              <img
                src={WordFile}
                alt="Word"
                onClick={() => triggerFilePicker([".doc", ".docx"])}
                style={{ width: 110, height: 138, cursor: "pointer" }}
              />
              <img
                src={ExcelFile}
                alt="Excel"
                onClick={() => triggerFilePicker([".xls", ".xlsx"])}
                style={{ width: 110, height: 138, cursor: "pointer" }}
              />
              <img
                src={PlainText}
                alt="Plain"
                onClick={() => triggerFilePicker(".txt")}
                style={{ width: 110, height: 138, cursor: "pointer" }}
              />
              <img
                src={RichTextFormat}
                alt="RTF"
                onClick={() => triggerFilePicker(".rtf")}
                style={{ width: 110, height: 138, cursor: "pointer" }}
              />
            </div>

            {fileList.length > 0 && (
              <>
                <p>{fileList[0]?.name}</p>
                <Progress percent={uploadProgress} />
              </>
            )}

            <div className="upload-actions">
              <Button
                type="primary"
                htmlType="button"
                block
                className="upload-btn"
                onClick={uploadFileToServer}
                disabled={fileList.length === 0}
                loading={uploading}
              >
                <img
                  src={UploadIcon}
                  style={{ width: 16, height: 16, marginRight: 5 }}
                />{" "}
                {t("media.modal.uploadDocument")}
              </Button>
              <Button
                onClick={() => setMediaTypeLocal(null)}
                className="goBack-btn"
              >
                <img src={LeftArrow} style={{ width: 16, height: 16 }} />{" "}
                {t("media.modal.goBack")}
              </Button>
            </div>
          </>
        ) : (
          <>
            <p style={{ marginBottom: 16 }}>{t("media.modal.pasteLink")}</p>
            <Input.Search
              placeholder={t("media.modal.enterDocumentLink")}
              disabled={attachingExt}
              allowClear={!attachingExt}
              enterButton={
                <Button type="primary" loading={attachingExt}>
                  {t("media.modal.attach")}
                </Button>
              }
              onSearch={attachExternal}
            />

            {attachingExt && (
              <div
                style={{
                  marginTop: 12,
                  display: "flex",
                  justifyContent: "center",
                }}
              >
                <Space>
                  <Spin size="small" />
                  <span>{t("media.modal.attaching")}</span>
                </Space>
              </div>
            )}

            <div
              style={{
                marginTop: 16,
                display: "flex",
                justifyContent: "center",
              }}
            >
              <Button
                onClick={() => setMediaTypeLocal(null)}
                className="goBack-btn"
                disabled={attachingExt}
              >
                <img src={LeftArrow} style={{ width: 16, height: 16 }} />{" "}
                {t("media.modal.goBack")}
              </Button>
            </div>
          </>
        )}
      </Modal>

      <AppModal
        open={isEditOpen}
        loading={false}
        onCancel={() => {
          setIsEditOpen(false);
          dispatch(setEditing(null));
          setEditName("");
        }}
        onConfirm={handleSaveEdit}
        title={t("media.editModal.title")}
        description={
          <>
            <div style={{ marginTop: 8 }}>
              <div
                style={{
                  fontWeight: 600,
                  marginBottom: 6,
                  textAlign: "left",
                  marginTop: "1rem",
                }}
              >
                <span style={{ color: "red", textAlign: "left" }}>*</span>{" "}
                {t("media.editModal.nameLabel")}
              </div>

              <Input
                placeholder={t("media.editModal.namePlaceholder")}
                value={editName}
                onChange={(e) => setEditName(e.target.value)}
                style={{ marginTop: "0.5rem" }}
              />
            </div>
          </>
        }
        subDescription={undefined}
        confirmText={t("media.editModal.save")}
        cancelText={t("media.editModal.cancel")}
        confirmType="primary"
        className="edit-media-modal"
        footer={
          <div className="footer-edit-media">
            <Button
              onClick={() => {
                setIsEditOpen(false);
                dispatch(setEditing(null));
                setEditName("");
              }}
              className="cancel-btn-footer"
            >
              {t("media.editModal.cancel")}
            </Button>

            <Button
              style={{ backgroundColor: "#4A1C96", color: "white" }}
              onClick={handleSaveEdit}
              loading={updateLoading}
            >
              {t("media.editModal.save")}
            </Button>
          </div>
        }
      />

      <AppModal
        open={!!deletingId}
        loading={deletingLoading}
        onCancel={() => dispatch(setDeleting(null))}
        onConfirm={handleConfirmDeleteDoc}
        title={t("media.deleteModal.title")}
        description={t("media.deleteModal.description")}
        subDescription={t("media.deleteModal.subDescription")}
        confirmText={t("media.deleteModal.confirm")}
        cancelText={t("media.deleteModal.cancel")}
        confirmType="primary"
        danger
        className="delete-doc-confirmation-modal"
      />
    </div>
  );
};

export default Media;
